import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const GeometryUploadModal = ({ isOpen, onClose, onUpload, routes }) => {
  const [selectedRouteId, setSelectedRouteId] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [previewData, setPreviewData] = useState(null);
  const fileInputRef = useRef(null);

  if (!isOpen) return null;

  const routeOptions = routes.map(route => ({
    value: route.id.toString(),
    label: route.route_name,
    description: `${route.start_point} → ${route.end_point}`
  }));

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    validateAndSetFile(file);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    validateAndSetFile(file);
  };

  const parseKML = (kmlText) => {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(kmlText, 'text/xml');
    
    // Check for parsing errors
    const parserError = xmlDoc.querySelector('parsererror');
    if (parserError) {
      throw new Error('Geçersiz KML formatı');
    }

    // Extract coordinates from LineString
    const coordinatesElement = xmlDoc.querySelector('LineString coordinates');
    if (!coordinatesElement) {
      throw new Error('KML dosyasında LineString bulunamadı');
    }

    const coordinatesText = coordinatesElement.textContent.trim();
    const coordinates = coordinatesText
      .split(/\s+/)
      .filter(coord => coord.trim())
      .map(coord => {
        const [lng, lat, alt] = coord.split(',').map(parseFloat);
        if (isNaN(lng) || isNaN(lat)) {
          throw new Error('Geçersiz koordinat formatı');
        }
        return [lng, lat];
      });

    if (coordinates.length < 2) {
      throw new Error('En az 2 koordinat gereklidir');
    }

    return {
      type: 'LineString',
      coordinates: coordinates
    };
  };

  const parseGeoJSON = (geoJsonText) => {
    const geoJson = JSON.parse(geoJsonText);
    
    let geometry;
    
    // Handle different GeoJSON structures
    if (geoJson.type === 'FeatureCollection') {
      // Extract first LineString feature
      const lineFeature = geoJson.features?.find(
        f => f.geometry?.type === 'LineString'
      );
      if (!lineFeature) {
        throw new Error('GeoJSON içinde LineString bulunamadı');
      }
      geometry = lineFeature.geometry;
    } else if (geoJson.type === 'Feature') {
      if (geoJson.geometry?.type !== 'LineString') {
        throw new Error('Sadece LineString geometrisi desteklenir');
      }
      geometry = geoJson.geometry;
    } else if (geoJson.type === 'LineString') {
      geometry = geoJson;
    } else {
      throw new Error('Desteklenmeyen GeoJSON formatı');
    }

    if (!geometry.coordinates || geometry.coordinates.length < 2) {
      throw new Error('En az 2 koordinat gereklidir');
    }

    // Validate coordinates
    geometry.coordinates.forEach((coord, index) => {
      if (!Array.isArray(coord) || coord.length < 2) {
        throw new Error(`Geçersiz koordinat formatı (index: ${index})`);
      }
      const [lng, lat] = coord;
      if (isNaN(lng) || isNaN(lat)) {
        throw new Error(`Geçersiz koordinat değeri (index: ${index})`);
      }
    });

    return geometry;
  };

  const validateAndSetFile = async (file) => {
    setUploadError('');
    setPreviewData(null);
    
    if (!file) return;

    const validExtensions = ['.json', '.geojson', '.kml', '.txt'];
    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

    if (!validExtensions.includes(fileExtension)) {
      setUploadError('Geçersiz dosya formatı. Sadece .json, .geojson, .kml veya .txt dosyaları kabul edilir.');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setUploadError('Dosya boyutu 10MB\'dan küçük olmalıdır.');
      return;
    }

    // Preview file content
    try {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const content = e.target.result;
          let geometry;

          if (fileExtension === '.kml') {
            geometry = parseKML(content);
          } else {
            // Try parsing as GeoJSON first
            try {
              geometry = parseGeoJSON(content);
            } catch {
              // Fallback to simple coordinate list
              const lines = content.split('\n').filter(line => line.trim());
              geometry = {
                type: 'LineString',
                coordinates: lines.map(line => {
                  const [lng, lat] = line.split(',').map(coord => parseFloat(coord.trim()));
                  if (isNaN(lng) || isNaN(lat)) {
                    throw new Error('Geçersiz koordinat formatı');
                  }
                  return [lng, lat];
                })
              };
            }
          }

          const distance = calculateDistance(geometry.coordinates);
          setPreviewData({
            pointCount: geometry.coordinates.length,
            distance: distance,
            geometry: geometry
          });
          setUploadFile(file);
        } catch (error) {
          setUploadError(error.message || 'Dosya önizlemesi oluşturulamadı.');
        }
      };
      reader.readAsText(file);
    } catch (error) {
      setUploadError('Dosya okunamadı.');
    }
  };

  const handleUpload = async () => {
    if (!selectedRouteId || !uploadFile || !previewData) {
      setUploadError('Lütfen rota seçin ve geçerli bir dosya yükleyin.');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setUploadError('');

    try {
      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 100);

      await onUpload({
        routeId: parseInt(selectedRouteId),
        geometry: previewData.geometry,
        distance_meters: previewData.distance,
        fileName: uploadFile.name
      });

      clearInterval(progressInterval);
      setUploadProgress(100);
      
      setTimeout(() => {
        handleClose();
      }, 500);
    } catch (error) {
      setUploadError(error.message || 'Yükleme sırasında hata oluştu.');
      setIsUploading(false);
    }
  };

  const calculateDistance = (coordinates) => {
    let totalDistance = 0;
    for (let i = 0; i < coordinates.length - 1; i++) {
      const [lng1, lat1] = coordinates[i];
      const [lng2, lat2] = coordinates[i + 1];
      totalDistance += getDistanceBetweenPoints(lat1, lng1, lat2, lng2);
    }
    return Math.round(totalDistance);
  };

  const getDistanceBetweenPoints = (lat1, lon1, lat2, lon2) => {
    const R = 6371e3;
    const φ1 = (lat1 * Math.PI) / 180;
    const φ2 = (lat2 * Math.PI) / 180;
    const Δφ = ((lat2 - lat1) * Math.PI) / 180;
    const Δλ = ((lon2 - lon1) * Math.PI) / 180;

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  };

  const handleClose = () => {
    setSelectedRouteId('');
    setUploadFile(null);
    setUploadProgress(0);
    setIsUploading(false);
    setUploadError('');
    setPreviewData(null);
    setIsDragging(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-2000 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="w-full max-w-2xl bg-card rounded-2xl shadow-xl overflow-hidden">
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-border">
          <h2 className="text-xl md:text-2xl font-bold text-foreground font-heading">
            Geometri Yükle
          </h2>
          <button
            onClick={handleClose}
            disabled={isUploading}
            className="flex items-center justify-center w-10 h-10 rounded-lg hover:bg-muted transition-smooth active-press disabled:opacity-50"
            aria-label="Kapat"
          >
            <Icon name="X" size={20} color="var(--color-foreground)" />
          </button>
        </div>

        <div className="p-4 md:p-6 space-y-6">
          <Select
            label="Rota Seçin"
            placeholder="Rota seçin..."
            options={routeOptions}
            value={selectedRouteId}
            onChange={setSelectedRouteId}
            searchable
            required
            disabled={isUploading}
          />

          <div className="space-y-2">
            <label className="block text-sm font-medium text-foreground">
              Geometri Dosyası
            </label>
            <p className="text-xs text-muted-foreground mb-2">
              Desteklenen formatlar: GeoJSON (.json, .geojson), KML (.kml), veya koordinat listesi (.txt)
            </p>
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`relative border-2 border-dashed rounded-lg p-8 md:p-12 text-center transition-smooth ${
                isDragging
                  ? 'border-primary bg-primary/5' :'border-border bg-muted/30 hover:bg-muted/50'
              } ${isUploading ? 'opacity-50 pointer-events-none' : ''}`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".json,.geojson,.kml,.txt"
                onChange={handleFileSelect}
                disabled={isUploading}
                className="hidden"
              />

              {uploadFile ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-center w-16 h-16 bg-success/10 rounded-full mx-auto">
                    <Icon name="FileCheck" size={32} color="var(--color-success)" />
                  </div>
                  <p className="text-sm font-medium text-foreground">{uploadFile.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {(uploadFile.size / 1024).toFixed(2)} KB
                  </p>
                  
                  {previewData && (
                    <div className="mt-4 p-4 bg-muted/50 rounded-lg space-y-2">
                      <h4 className="text-sm font-semibold text-foreground">Önizleme</h4>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="flex items-center gap-2">
                          <Icon name="MapPin" size={14} color="var(--color-primary)" />
                          <span className="text-muted-foreground">{previewData.pointCount} nokta</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Icon name="Ruler" size={14} color="var(--color-primary)" />
                          <span className="text-muted-foreground">
                            {(previewData.distance / 1000).toFixed(2)} km
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setUploadFile(null);
                      setPreviewData(null);
                    }}
                    disabled={isUploading}
                  >
                    Değiştir
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mx-auto">
                    <Icon name="Upload" size={32} color="var(--color-primary)" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground mb-1">
                      Dosyayı sürükleyip bırakın
                    </p>
                    <p className="text-xs text-muted-foreground">veya</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Dosya Seç
                  </Button>
                </div>
              )}
            </div>
          </div>

          {isUploading && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Yükleniyor...</span>
                <span className="font-medium text-foreground">{uploadProgress}%</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
                <div
                  className="h-full bg-primary transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}

          {uploadError && (
            <div className="flex items-start gap-3 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
              <Icon name="AlertCircle" size={20} color="var(--color-destructive)" className="flex-shrink-0 mt-0.5" />
              <p className="text-sm text-destructive">{uploadError}</p>
            </div>
          )}

          <div className="flex items-start gap-3 p-4 bg-primary/5 border border-primary/20 rounded-lg">
            <Icon name="Info" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
            <div className="text-xs text-muted-foreground space-y-1">
              <p>• Dosya formatı: JSON, GeoJSON veya koordinat listesi (TXT)</p>
              <p>• Koordinat formatı: [boylam, enlem] veya "boylam,enlem"</p>
              <p>• Minimum 2 koordinat noktası gereklidir</p>
              <p>• Mesafe otomatik olarak hesaplanacaktır</p>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-4 md:p-6 border-t border-border bg-muted/30">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isUploading}
          >
            İptal
          </Button>
          <Button
            variant="default"
            iconName="Upload"
            iconPosition="left"
            onClick={handleUpload}
            loading={isUploading}
            disabled={!selectedRouteId || !uploadFile || !previewData}
          >
            Yükle
          </Button>
        </div>
      </div>
    </div>
  );
};

export default GeometryUploadModal;